import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { StatCard } from '@/components/ui';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Webhook as WebhookIcon,
  CheckCircle as SuccessIcon,
} from '@mui/icons-material';
import { SUPERADMIN_STATUS_GROUPS } from '../../constants';
import type { SuperadminWebhookMetrics, SuperadminWebhookSummaryCardsProps, SuperadminWebhook } from '../../types';

export const SuperadminWebhookSummaryCards: React.FC<SuperadminWebhookSummaryCardsProps> = ({ webhooks }) => {
  // Calculate webhook statistics dynamically
  const stats: SuperadminWebhookMetrics = useMemo(() => {
    const totalCount = webhooks.length;

    const successfulWebhooks = webhooks.filter((webhook: SuperadminWebhook) =>
      SUPERADMIN_STATUS_GROUPS.SUCCESSFUL.includes(webhook.webhook_status as any)
    );
    const failedWebhooks = webhooks.filter((webhook: SuperadminWebhook) =>
      SUPERADMIN_STATUS_GROUPS.FAILED.includes(webhook.webhook_status as any)
    );
    const pendingWebhooks = webhooks.filter((webhook: SuperadminWebhook) =>
      SUPERADMIN_STATUS_GROUPS.PENDING.includes(webhook.webhook_status as any)
    );

    const totalDelivered = successfulWebhooks.length;
    const totalFailed = failedWebhooks.length;
    const totalPending = pendingWebhooks.length;

    return {
      totalCount,
      totalDelivered,
      totalFailed,
      totalPending,
      deliveryRate: totalCount > 0 ? (successfulWebhooks.length / totalCount) * 100 : 0,
      failureRate: totalCount > 0 ? (failedWebhooks.length / totalCount) * 100 : 0,
      pendingRate: totalCount > 0 ? (pendingWebhooks.length / totalCount) * 100 : 0,
    };
  }, [webhooks]);

  return (
    <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(280px, 1fr))" gap={3} mb={4}>
      {/* Total Webhooks Card */}
      <StatCard
        title="TOTAL WEBHOOKS"
        subtitle="All webhook events across merchants"
        value={stats.totalCount.toString()}
        percentage={Math.round(stats.deliveryRate)}
        percentageLabel="Success Rate"
        trend="up"
        icon={<WebhookIcon />}
        color="primary"
        size="medium"
      />

      {/* Delivered Webhooks Card */}
      <StatCard
        title="DELIVERED WEBHOOKS"
        subtitle="Successfully delivered webhooks"
        value={stats.totalDelivered.toString()}
        percentage={Math.round(stats.deliveryRate)}
        percentageLabel="Success Rate"
        trend="up"
        icon={<SuccessIcon />}
        color="success"
        size="medium"
      />

      {/* Failed Webhooks Card */}
      <StatCard
        title="FAILED WEBHOOKS"
        subtitle="Failed webhook deliveries"
        value={stats.totalFailed.toString()}
        percentage={Math.round(stats.failureRate)}
        percentageLabel="Failure Rate"
        trend="down"
        icon={<TrendingDownIcon />}
        color="error"
        size="medium"
      />

      {/* Pending Webhooks Card */}
      <StatCard
        title="PENDING WEBHOOKS"
        subtitle="Webhooks awaiting delivery"
        value={stats.totalPending.toString()}
        percentage={Math.round(stats.pendingRate)}
        percentageLabel="Pending Rate"
        trend="neutral"
        icon={<TrendingUpIcon />}
        color="warning"
        size="medium"
      />
    </Box>
  );
};

export default SuperadminWebhookSummaryCards;
